/*
 * Copyright (c) 2024, King Living
 * Portal image lightbox.
 * - default (Complex PDP): mobile full viewport; desktop band below header (z-index 1098).
 * - simplePdp variant: full viewport on all breakpoints (z-index 2000 covers header).
 * - Optional `desktopLightboxRightInsetPx` applies `right` on the stage so centered content clears
 *   the fixed config panel (no global CSS variables).
 */

const ImageZoomModal = {
  parts: ['root', 'backdrop', 'stage', 'inner', 'closeButton', 'image'],

  baseStyle: {
    backdrop: {
      WebkitBackdropFilter: 'blur(16px)',
      backdropFilter: 'blur(16px)',
      bg: 'rgba(0, 0, 0, 0.20)',
      bottom: 0,
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 0,
    },
    closeButton: {
      _focusVisible: {
        boxShadow: 'outline',
        outline: 'none',
      },
      _hover: {bg: '#F0EDE7'},
      alignItems: 'center',
      bg: '#F7F5F0',
      borderRadius: '4px',
      color: 'gray.800',
      display: 'flex',
      flexShrink: 0,
      gap: '8px',
      h: {'base': '54px', 'lg': '54px'},
      minW: {'base': '52px', 'lg': '52px'},
      justifyContent: 'center',
      p: '16px',
      position: {base: 'absolute', lg: 'fixed'},
      right: {base: -2, lg: '20px'},
      top: {base: -2, lg: 'calc(var(--header-height, 0px) + 48px)'},
      w: {'base': '52px', 'lg': '52px'},
      zIndex: 10,
      sx: {
        '& .chakra-icon svg, & svg': {
          h: '16.166px',
          minW: '16.166px',
        },
      },
    },
    image: {
      display: 'block',
      h: 'auto',
      maxH: {
        base:
          'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 48px)',
        lg: 'calc(100dvh - var(--header-height, 0px) - env(safe-area-inset-bottom, 0px) - 96px)',
      },
      maxW: {
        base:
          'calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - 32px)',
        lg: '100%',
      },
      objectFit: 'contain',
      position: 'relative',
      w: 'auto',
      zIndex: 0,
    },
    inner: {
      display: 'inline-block',
      lineHeight: 0,
      maxW: '100%',
      pointerEvents: 'auto',
      position: 'relative',
      verticalAlign: 'middle',
    },
    root: {
      bottom: 0,
      left: 0,
      pointerEvents: 'auto',
      position: 'fixed',
      right: {base: 0, lg: 0},
      top: {base: 0, lg: 'var(--header-height, 0px)'},
      zIndex: {base: 2000, lg: 1098},
    },
    stage: {
      alignItems: 'center',
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 1,
    },
  },

  variants: {
    simplePdp: {
      closeButton: {
        top: {base: -2, lg: '48px'},
      },
      image: {
        maxH: {
          lg: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 96px)',
        },
      },
      root: {
        top: 0,
        zIndex: 2000,
      },
    },
  },
};

export default ImageZoomModal;
